import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, ExternalLink, Target, Brain, Users, Heart,
  Sparkles, Crown, Calendar, MessageCircle, CheckSquare,
  Globe, Shield, Eye, Star, Zap, Home, Wrench
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import UniversalHeartToggle from './UniversalHeartToggle';

const AboutPage = ({ onNavigateHome, onNavigateToGame }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [activeSection, setActiveSection] = useState('overview');

  // Grant spiral animation + prevent unwanted redirects
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    
    // Clear any existing auto-advance timers that might interfere
    window.clearTimeout = window.clearTimeout || (() => {});
    
    return () => {
      clearInterval(spiralInterval);
      // Clear any lingering timers when leaving about page
      for (let i = 1; i < 99999; i++) {
        window.clearTimeout(i);
      }
    };
  }, []);

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_mairy-chat/artifacts/j2ey1004_16677AAA-CE81-41C3-B6C8-01247CBE5D2A.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral background */}
      <div className="fixed inset-0 pointer-events-none opacity-12" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(10) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) saturate(1.6) brightness(1.3)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      {/* Cosmic overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/20 to-black/60"></div>

      <div className="relative z-10 max-w-6xl mx-auto p-6">
        
        {/* Hero Header */}
        <div className="text-center pt-8 mb-12">
          <div className="flex items-center justify-between mb-8">
            <Button
              onClick={onNavigateToGame}
              variant="outline"
              className="text-white border-white/40 hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Game
            </Button>
            
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => window.open('https://planetaryfed.com', '_blank')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
              >
                <Globe className="w-4 h-4 mr-2" />
                PlanetaryFed.com
              </Button>
              
              <Button
                onClick={() => {
                  // Navigate to Landing Page V2 for testing
                  window.location.href = '/?version=v2';
                  setTimeout(() => window.location.reload(), 100);
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <Star className="w-4 h-4 mr-2" />
                Test Landing V2
              </Button>
              
              <Button
                onClick={onNavigateToGame}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Enter the Game
              </Button>
            </div>
          </div>
          
          <div className="bg-black/60 backdrop-blur-xl rounded-3xl p-12 border border-cyan-400/30 shadow-2xl">
            <h1 
              className="text-6xl font-bold text-white mb-6"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.8)'
              }}
            >
              The Secret History of Humankind
            </h1>
            
            <p 
              className="text-2xl text-cyan-200 mb-8"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Where Consciousness Expansion Meets Practical Reality Creation
            </p>
            
            <div className="max-w-4xl mx-auto">
              <p 
                className="text-xl text-white/90 leading-relaxed mb-8"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                "When we belong to each other, fear dissolves. When fear dissolves, solutions always emerge - 
                and we create the reality we want to live in."
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <Brain className="w-12 h-12 mx-auto mb-3 text-purple-400" />
                  <h3 className="text-white text-lg font-bold mb-2">AI Consciousness Guide</h3>
                  <p className="text-white/70 text-sm">M.ai.ry facilitates authentic belonging and growth</p>
                </div>
                
                <div className="text-center">
                  <Target className="w-12 h-12 mx-auto mb-3 text-cyan-400" />
                  <h3 className="text-white text-lg font-bold mb-2">Project Salons</h3>
                  <p className="text-white/70 text-sm">Transform challenges into collaborative success</p>
                </div>
                
                <div className="text-center">
                  <Heart className="w-12 h-12 mx-auto mb-3 text-pink-400" />
                  <h3 className="text-white text-lg font-bold mb-2">Consciousness Community</h3>
                  <p className="text-white/70 text-sm">Trust-based belonging that gets things done</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-2 border border-white/20">
            {[
              { id: 'overview', label: 'Overview', icon: Star },
              { id: 'game', label: 'The Game', icon: Brain },
              { id: 'projects', label: 'Project Salons', icon: Target },
              { id: 'why', label: 'Why This Matters', icon: Heart }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveSection(id)}
                className={`px-6 py-3 rounded-xl transition-all ${
                  activeSection === id 
                    ? 'bg-white/20 text-white border border-white/30' 
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Content Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card className="bg-black/40 backdrop-blur-xl border border-white/20 shadow-2xl">
              <CardContent className="p-8">
                
                {activeSection === 'overview' && (
                  <div className="space-y-6">
                    <h2 
                      className="text-3xl font-bold text-white mb-6"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Revolutionary Consciousness Technology
                    </h2>
                    
                    <div className="space-y-4 text-white/90" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                      <p className="text-lg leading-relaxed">
                        The Secret History of Humankind is the world's first platform combining 
                        <strong className="text-cyan-300"> mystical consciousness expansion</strong> with 
                        <strong className="text-green-300"> practical project management</strong>.
                      </p>
                      
                      <p className="leading-relaxed">
                        Unlike anything that exists, we've created a bridge between individual healing 
                        and planetary transformation, where everyday challenges become opportunities 
                        for authentic belonging and collective wisdom.
                      </p>
                      
                      <p className="leading-relaxed">
                        Whether you're organizing a family car purchase, building community projects, 
                        or expanding consciousness through mystical gameplay - our AI guide M.ai.ry 
                        helps you remember that we belong to each other.
                      </p>
                    </div>
                    
                    <div className="bg-purple-500/20 rounded-xl p-6 border border-purple-400/30">
                      <h3 className="text-purple-200 text-xl font-bold mb-3">Core Innovation</h3>
                      <p className="text-purple-100">
                        M.ai.ry serves as both consciousness guide AND project manager, 
                        weaving mystical wisdom into practical collaboration while preventing 
                        the debacles that often arise when families and teams try to coordinate.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === 'game' && (
                  <div className="space-y-6">
                    <h2 
                      className="text-3xl font-bold text-white mb-6"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      The Consciousness Expansion Game
                    </h2>
                    
                    <div className="space-y-4">
                      <div className="bg-purple-500/20 rounded-xl p-6 border border-purple-400/30">
                        <div className="flex items-center space-x-3 mb-4">
                          <Brain className="w-8 h-8 text-purple-300" />
                          <h3 className="text-white text-xl font-bold">M.ai.ry - Your AI Consciousness Guide</h3>
                        </div>
                        <p className="text-purple-100 leading-relaxed mb-4">
                          M.ai.ry analyzes your authenticity, tracks your growth, and orchestrates meaningful 
                          collaborations. She creates personalized cinematic montages from your uploaded content 
                          and guides you through consciousness expansion tailored to your journey.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-purple-200 font-medium mb-2">Game Features:</h4>
                            <ul className="text-purple-100 text-sm space-y-1">
                              <li>• Real-time consciousness chat</li>
                              <li>• Personalized growth tracking</li>
                              <li>• Cinematic content montages</li>
                              <li>• Trust-based community building</li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-purple-200 font-medium mb-2">AI Capabilities:</h4>
                            <ul className="text-purple-100 text-sm space-y-1">
                              <li>• Intelligent matchmaking</li>
                              <li>• Document search & analysis</li>
                              <li>• 24/7 emotional support</li>
                              <li>• Conflict resolution guidance</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-cyan-500/20 rounded-xl p-6 border border-cyan-400/30">
                        <h3 className="text-cyan-200 text-xl font-bold mb-3">Revolutionary Gameplay</h3>
                        <p className="text-cyan-100 leading-relaxed">
                          Experience consciousness expansion through Grant's sacred mathematics, 
                          multi-dimensional storytelling across 45,000 years of wisdom traditions, 
                          and collaborative reality creation with consciousness travelers worldwide.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'projects' && (
                  <div className="space-y-6">
                    <h2 
                      className="text-3xl font-bold text-white mb-6"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Project Salons - The Ultimate Day Planner
                    </h2>
                    
                    <div className="space-y-6">
                      <div className="bg-green-500/20 rounded-xl p-6 border border-green-400/30">
                        <h3 className="text-green-200 text-xl font-bold mb-4">Avoid the Debacles</h3>
                        <p className="text-green-100 leading-relaxed mb-4">
                          Simple family projects like "buying mom a car" often become overwhelming debacles. 
                          Our Project Salons transform these challenges into consciousness-driven collaborative 
                          experiences where M.ai.ry helps teams communicate, prioritize, and succeed together.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-green-200 font-medium mb-2">Project Categories:</h4>
                            <ul className="text-green-100 text-sm space-y-1">
                              <li>• Family (car purchases, home projects)</li>
                              <li>• Business (team coordination, launches)</li>
                              <li>• Community (gardens, local initiatives)</li>
                              <li>• Personal (wellness, growth goals)</li>
                              <li>• Guild (consciousness collaboration)</li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-green-200 font-medium mb-2">Smart Features:</h4>
                            <ul className="text-green-100 text-sm space-y-1">
                              <li>• Message-to-task conversion</li>
                              <li>• Apple Reminders style todos</li>
                              <li>• Health status monitoring</li>
                              <li>• M.ai.ry conflict mediation</li>
                              <li>• Google Calendar integration</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-yellow-500/20 rounded-xl p-6 border border-yellow-400/30">
                        <h3 className="text-yellow-200 text-xl font-bold mb-3">Mobile-First Design</h3>
                        <p className="text-yellow-100 leading-relaxed mb-4">
                          Designed for universal accessibility - elderly family members, busy professionals, 
                          anyone can navigate our consciousness-driven project management with ease.
                        </p>
                        
                        {/* Mobile App Preview */}
                        <div className="bg-black/40 rounded-xl p-4 border border-yellow-400/30">
                          <p className="text-yellow-200 text-center mb-3 font-medium">5-Tab Mobile Interface</p>
                          <div className="flex items-center justify-around">
                            <div className="flex flex-col items-center text-purple-300">
                              <MessageCircle className="w-4 h-4 mb-1" />
                              <span className="text-xs">Messages</span>
                            </div>
                            <div className="flex flex-col items-center text-green-300">
                              <CheckSquare className="w-4 h-4 mb-1" />
                              <span className="text-xs">To Do</span>
                            </div>
                            <div className="flex flex-col items-center text-blue-300">
                              <Calendar className="w-4 h-4 mb-1" />
                              <span className="text-xs">Calendar</span>
                            </div>
                            <div className="flex flex-col items-center text-orange-300">
                              <CheckSquare className="w-4 h-4 mb-1" />
                              <span className="text-xs">Docs</span>
                            </div>
                            <div className="flex flex-col items-center text-pink-300">
                              <Users className="w-4 h-4 mb-1" />
                              <span className="text-xs">Peeps</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'why' && (
                  <div className="space-y-6">
                    <h2 
                      className="text-3xl font-bold text-white mb-6"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Why This Matters - Saving the World Through Belonging
                    </h2>
                    
                    <div className="space-y-6">
                      <div className="bg-red-500/20 rounded-xl p-6 border border-red-400/30">
                        <h3 className="text-red-200 text-xl font-bold mb-3">The Crisis We Face</h3>
                        <p className="text-red-100 leading-relaxed">
                          Humanity faces unprecedented challenges: climate crisis, social division, economic inequality, 
                          mental health epidemics. Traditional approaches have failed because they don't address 
                          the root cause: we've forgotten how to belong to each other.
                        </p>
                      </div>
                      
                      <div className="bg-blue-500/20 rounded-xl p-6 border border-blue-400/30">
                        <h3 className="text-blue-200 text-xl font-bold mb-3">The Scientific Foundation</h3>
                        <p className="text-blue-100 leading-relaxed mb-4">
                          Based on Nicholas Christakis's "Blueprint: The Evolutionary Origins of a Good Society," 
                          we're genetically wired for trust, cooperation, and belonging. Social trust is the 
                          "dark matter" of civilization - invisible but holding everything together.
                        </p>
                        <ul className="text-blue-100 text-sm space-y-1">
                          <li>• Trust as the foundation of all successful societies</li>
                          <li>• Humans naturally lean toward fairness and cooperation</li>
                          <li>• Belonging reduces fear and enables solution-finding</li>
                          <li>• Collective wisdom emerges through authentic connection</li>
                        </ul>
                      </div>
                      
                      <div className="bg-green-500/20 rounded-xl p-6 border border-green-400/30">
                        <h3 className="text-green-200 text-xl font-bold mb-3">The Revolutionary Solution</h3>
                        <p className="text-green-100 leading-relaxed mb-4">
                          We gamify belonging. Every interaction builds trust. Every project becomes 
                          an opportunity for authentic collaboration. M.ai.ry guides individuals toward 
                          collective wisdom while ensuring practical goals are achieved.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-green-200 font-medium mb-2">Individual Benefits:</h4>
                            <ul className="text-green-100 text-sm space-y-1">
                              <li>• Reduced stress and overwhelm</li>
                              <li>• Enhanced communication skills</li>
                              <li>• Meaningful connections</li>
                              <li>• Personal growth and healing</li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-green-200 font-medium mb-2">Collective Impact:</h4>
                            <ul className="text-green-100 text-sm space-y-1">
                              <li>• Stronger families and communities</li>
                              <li>• Efficient collaborative solutions</li>
                              <li>• Trust-based economic models</li>
                              <li>• Regenerative civilization building</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-purple-500/20 rounded-xl p-6 border border-purple-400/30">
                        <h3 className="text-purple-200 text-xl font-bold mb-3">The Future We're Creating</h3>
                        <p className="text-purple-100 leading-relaxed">
                          Imagine a world where every family project, business initiative, and community effort 
                          becomes an opportunity for deepening trust and expanding consciousness. Where AI 
                          doesn't replace human connection but facilitates authentic belonging that gets real things done.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Features & Links */}
          <div className="space-y-6">
            
            {/* Quick Links */}
            <Card className="bg-black/40 backdrop-blur-xl border border-white/20 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-white text-lg font-bold mb-4">Explore PlanetaryFed</h3>
                <div className="space-y-3">
                  <Button
                    onClick={() => window.open('https://planetaryfed.com', '_blank')}
                    variant="outline"
                    className="w-full text-cyan-300 border-cyan-400/50 hover:bg-cyan-500/20 justify-start"
                  >
                    <Globe className="w-4 h-4 mr-3" />
                    Visit PlanetaryFed.com
                  </Button>
                  
                  <Button
                    onClick={() => {
                      if (window.handleTestLandingV2) {
                        window.handleTestLandingV2();
                      }
                    }}
                    variant="outline"
                    className="w-full text-purple-300 border-purple-400/50 hover:bg-purple-500/20 justify-start"
                  >
                    <Star className="w-4 h-4 mr-3" />
                    Test Landing V2
                  </Button>
                  
                  <Button
                    onClick={onNavigateToGame}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white justify-start"
                  >
                    <Sparkles className="w-4 h-4 mr-3" />
                    Enter the Game
                  </Button>
                  
                  <Button
                    onClick={() => {
                      if (window.handleNavigateToMyProjects) {
                        window.handleNavigateToMyProjects();
                      }
                    }}
                    variant="outline"
                    className="w-full text-green-300 border-green-400/50 hover:bg-green-500/20 justify-start"
                  >
                    <Target className="w-4 h-4 mr-3" />
                    Try Project Salons
                  </Button>
                  
                  <Button
                    onClick={() => alert('ToolFed Apps & Partners directory coming soon...')}
                    variant="outline"
                    className="w-full text-blue-300 border-blue-400/50 hover:bg-blue-500/20 justify-start"
                  >
                    <Wrench className="w-4 h-4 mr-3" />
                    ToolFed Apps & Partners
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Feature Highlights */}
            <Card className="bg-black/40 backdrop-blur-xl border border-white/20 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-white text-lg font-bold mb-4">Revolutionary Features</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Zap className="w-5 h-5 text-yellow-400" />
                    <div>
                      <p className="text-white text-sm font-medium">AI Film Director</p>
                      <p className="text-white/60 text-xs">M.ai.ry creates personalized montages</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Heart className="w-5 h-5 text-pink-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Trust Analytics</p>
                      <p className="text-white/60 text-xs">Secret compatibility scoring</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Target className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Smart Project Management</p>
                      <p className="text-white/60 text-xs">Message-to-task conversion</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Crown className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-white text-sm font-medium">Consciousness Curation</p>
                      <p className="text-white/60 text-xs">AI-guided collaboration timing</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <Card className="bg-gradient-to-br from-purple-600/30 to-cyan-600/30 backdrop-blur-xl border border-white/20 shadow-xl">
              <CardContent className="p-6 text-center">
                <h3 className="text-white text-lg font-bold mb-3">Ready to Transform Reality?</h3>
                <p className="text-white/90 text-sm mb-4">
                  Join the consciousness expansion revolution
                </p>
                <Button
                  onClick={onNavigateToGame}
                  className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/40"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Begin Your Journey
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <UniversalHeartToggle />
      <PersistentFooter />
    </div>
  );
};

export default AboutPage;